/**
 * Drawer panel that manages feature flags for the customer org linked to an
 * account card.
 *
 * Shows only flags that are enabled — or were enabled and have since been
 * disabled ("was enabled" history, kept via soft-deleted gates) — never the
 * full flag catalog. New flags are enabled via a search-and-add combobox.
 * Superadmin only, same as LinkOrgPicker: the accounts board is an internal
 * CRM surface and gate mutations go through superadmin endpoints.
 */
import React from 'react';
import {
  ComboBox,
  Flex,
  Heading,
  Switch,
  TagBadge,
  Text,
  useToast,
  type ComboBoxOption,
} from '@chatsdet/ui';
import { Flag } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useDisableFlagForOrgMutation,
  useEnableFlagForOrgMutation,
  useFeatureFlagsQuery,
  useOrgFeatureFlagsQuery,
} from '../../../ui/pages/superadmin/useSuperadminData';
import type { Card } from '../../cards/hooks/useCards';

interface Props {
  card: Card;
}

export function CardFeatureFlagsPanel({ card }: Props) {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const { toast } = useToast();

  const [inputValue, setInputValue] = React.useState('');

  const linkedOrg = card.linkedOrg;
  const orgFlagsQuery = useOrgFeatureFlagsQuery(isSuperadmin ? linkedOrg?.id : null);
  const allFlagsQuery = useFeatureFlagsQuery();
  const enableFlag = useEnableFlagForOrgMutation();
  const disableFlag = useDisableFlagForOrgMutation();

  const orgFlags = orgFlagsQuery.data?.flags ?? [];

  // Search-and-add options: every flag not already listed for this org.
  const addOptions: ComboBoxOption[] = React.useMemo(() => {
    if (!allFlagsQuery.data?.flags) return [];
    const listed = new Set(orgFlags.map((flag) => flag.name));
    return allFlagsQuery.data.flags
      .filter((flag) => !listed.has(flag.name))
      .map((flag) => ({ id: flag.name, label: flag.name, textValue: flag.name }));
  }, [allFlagsQuery.data?.flags, orgFlags]);

  if (!isSuperadmin) return null;

  if (!linkedOrg) {
    return (
      <Flex direction="column" gap="sm" className="mt-2">
        <Heading level={5} className="text-sm">
          Feature flags
        </Heading>
        <Text size="xs" className="text-muted-foreground">
          Link a customer organization to manage its feature flags.
        </Text>
      </Flex>
    );
  }

  const handleToggle = (flagName: string, enable: boolean) => {
    const mutation = enable ? enableFlag : disableFlag;
    mutation.mutate(
      { flagName, orgId: linkedOrg.id },
      {
        onSuccess: () =>
          toast.success(`${flagName} ${enable ? 'enabled' : 'disabled'} for ${linkedOrg.name}`),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Failed to update flag'),
      }
    );
  };

  const handleAdd = (key: string | null) => {
    if (!key) return;
    enableFlag.mutate(
      { flagName: key, orgId: linkedOrg.id },
      {
        onSuccess: () => {
          toast.success(`${key} enabled for ${linkedOrg.name}`);
          setInputValue('');
        },
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Failed to enable flag'),
      }
    );
  };

  return (
    <Flex direction="column" gap="sm" className="mt-2">
      <Heading level={5} className="text-sm">
        Feature flags
      </Heading>

      {orgFlags.length === 0 && !orgFlagsQuery.isLoading && (
        <Text size="xs" className="text-muted-foreground">
          No feature flags enabled for {linkedOrg.name} yet.
        </Text>
      )}

      {orgFlags.map((flag) => (
        <Flex key={flag.flagId} gap="sm" align="center" justify="between">
          <Flex gap="xs" align="center" wrap className="min-w-0">
            <Flag size={12} className="shrink-0 text-muted-foreground" />
            <Text size="xs" className="truncate font-mono" title={flag.description ?? flag.name}>
              {flag.name}
            </Text>
            {!flag.enabled && <TagBadge name="was enabled" color="gray" hideDot />}
          </Flex>
          <Switch
            size="sm"
            aria-label={`Toggle ${flag.name} for ${linkedOrg.name}`}
            isSelected={flag.enabled}
            isDisabled={enableFlag.isPending || disableFlag.isPending}
            onChange={(selected) => handleToggle(flag.name, selected)}
          />
        </Flex>
      ))}

      <ComboBox
        items={addOptions}
        placeholder="Search flags to enable…"
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={(key) => handleAdd(key as string | null)}
        className="w-[260px]"
        menuTrigger="focus"
      />
    </Flex>
  );
}
